import { PropsWithChildren, Suspense } from 'react'
import { useEffect } from 'react'
import ReactGA from 'react-ga4'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import { Box } from '@chakra-ui/react'
import { useVariant } from '@unleash/proxy-client-react'
import { z } from 'zod'

import { startRumSessionMonitoring } from '~lib/monitoring'
import { GOOGLE_ANALYTICS_TAG_ID } from '~constants/config'
import { routes } from '~constants/routes'
import { lazyImport } from '~utils/lazyImport'
import { useBrowserId } from '~stores/user-settings'

import { PrivacyPage, TermsOfUsePage } from '~pages/routes'

import { BaseElement } from './BaseElement'

const { SearchRoutes } = lazyImport(
  () => import('~features/search'),
  'SearchRoutes',
)
const { HealthRoutes } = lazyImport(
  () => import('~features/health'),
  'HealthRoutes',
)

const router = createBrowserRouter([
  {
    path: routes.health,
    element: <HealthRoutes />,
  },
  {
    path: `${routes.privacy}`,
    element: <BaseElement element={<PrivacyPage />} />,
  },
  {
    path: `${routes.termsOfUse}`,
    element: <BaseElement element={<TermsOfUsePage />} />,
  },
  {
    path: '*',
    element: <BaseElement element={<SearchRoutes />} />,
  },
])

const WithSuspense = ({ children }: PropsWithChildren) => (
  <Suspense fallback={<Box bg="neutral.100" minH="$100vh" w="100vw" />}>
    {children}
  </Suspense>
)

const rumSessionReplaySampleRatePayloadSchema = z.coerce
  .number()
  .min(0)
  .max(100)

export const AppRouter = (): JSX.Element => {
  const browserId = useBrowserId()

  const { feature_enabled: customSessionReplaySampleRateEnabled, payload } =
    useVariant('session-replay-sample-rate')

  // Initialize monitoring
  useEffect(() => {
    let sessionReplaySampleRate = undefined
    if (customSessionReplaySampleRateEnabled && payload) {
      const result = rumSessionReplaySampleRatePayloadSchema.safeParse(
        payload.value,
      )

      if (result.success) {
        sessionReplaySampleRate = result.data
      }
    }

    startRumSessionMonitoring({ browserId, sessionReplaySampleRate })
    if (GOOGLE_ANALYTICS_TAG_ID) {
      ReactGA.initialize(GOOGLE_ANALYTICS_TAG_ID)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <WithSuspense>
      <RouterProvider router={router} />
    </WithSuspense>
  )
}
